import classNames from 'classnames';
import type { HTMLAttributes } from 'react';
import React, { useRef } from 'react';
import * as styles from './PostModalViewMoveable.module.scss';

interface PostModalViewMoveableProps extends HTMLAttributes<HTMLDivElement> {
  onDraggingStart?: (position: [number, number]) => void;
  onDraggingMove?: (position: [number, number]) => void;
  onDraggingEnd?: () => void;
}

const PostModalViewMoveable = ({
  onDraggingStart,
  onDraggingMove,
  onDraggingEnd,
  ...props
}: PostModalViewMoveableProps) => {
  const draggingStateRef = useRef<{
    capturedElement: Element;
    hasCaptured: boolean;
  } | null>(null);

  if (!onDraggingStart || !onDraggingMove || !onDraggingEnd) {
    return <div {...props} />;
  }

  const handleStartDragging = (event: React.PointerEvent) => {
    if (!(event.target instanceof Element)) return;

    if (window.getComputedStyle(event.target).cursor !== 'move') return;

    draggingStateRef.current = {
      capturedElement: event.target,
      hasCaptured: false,
    };
    onDraggingStart?.([event.screenX, event.screenY]);
  };

  const handleEndDragging = (event: React.PointerEvent) => {
    if (!draggingStateRef.current) return;

    if (draggingStateRef.current.hasCaptured) {
      draggingStateRef.current.capturedElement.releasePointerCapture(
        event.pointerId,
      );
    }
    draggingStateRef.current = null;
    onDraggingEnd?.();
  };

  const handleDragging = (event: React.PointerEvent) => {
    if (!draggingStateRef.current) return;

    if (!draggingStateRef.current.hasCaptured) {
      draggingStateRef.current.hasCaptured = true;
      draggingStateRef.current.capturedElement.setPointerCapture(
        event.pointerId,
      );
    }

    onDraggingMove?.([event.screenX, event.screenY]);
  };

  return (
    <div
      {...props}
      className={classNames(props.className, {
        [styles.moveable]: !!onDraggingStart,
      })}
      onPointerMove={handleDragging}
      onPointerDown={handleStartDragging}
      onPointerLeave={handleEndDragging}
      onPointerUp={handleEndDragging}
    />
  );
};

export default PostModalViewMoveable;
